/**
 * UI Rendering Functions
 * Handles all HTML rendering for departure boards and display items
 */

import { getConfig } from '@/config';
import { getDirectionsUrl } from '@/utils/maps-link';
import { FavoritesManager } from '@/utils/favorites';
import { escapeHtml } from '@/utils/html';
import type { Departure, DepartureBoard, TrainDeparture, TrainDepartureBoard } from '@/types';
import {
    type DisplayItem,
    getCurrentSearchRadius,
    hasReachedMaxRadius,
    setDisplayedAtcoCodes,
    setAllDisplayItems,
    getSelectedRoutes,
} from '@/core/app-state';

/**
 * Get bearing direction label
 */
function getBearingLabel(bearing: string | undefined): string {
    if (!bearing) return '';
    const labels: Record<string, string> = {
        N: 'Heading north',
        S: 'Heading south',
        E: 'Heading east',
        W: 'Heading west',
        NE: 'Heading north-east',
        NW: 'Heading north-west',
        SE: 'Heading south-east',
        SW: 'Heading south-west',
    };
    return labels[bearing.toUpperCase()] || escapeHtml(bearing);
}

/**
 * Format distance for display
 */
function formatDistance(meters: number): string {
    if (meters >= 1000) {
        return `${(meters / 1000).toFixed(1)}km away`;
    }
    return `${Math.round(meters)}m away`;
}

/**
 * Format a search radius for display (no "away" suffix)
 */
function formatRadius(meters: number): string {
    if (meters >= 1000) {
        return `${(meters / 1000).toFixed(1)}km`;
    }
    return `${Math.round(meters)}m`;
}

/** Favorite toggle icons (outline / filled star), shared with event-handlers.ts */
export const FAVORITE_ICON = '☆';
export const FAVORITED_ICON = '★';

/** Labels for the "show more stops" button, shared with event-handlers.ts */
export const SHOW_MORE_LABEL = 'Search further away';
export const SHOW_MORE_BUSY_LABEL = 'Searching further away…';

/** Occupancy badge label and style class, keyed by SIRI-VM occupancy value */
const OCCUPANCY_DISPLAY: Record<string, { label: string; className: string }> = {
    seatsAvailable: { label: 'Seats available', className: 'seats-available' },
    standingAvailable: { label: 'Standing only', className: 'standing-only' },
    full: { label: 'Full', className: 'full' },
};

/**
 * Render a single departure row
 */
function renderDeparture(departure: Departure): string {
    const timeClass = departure.status === 'delayed' ? 'time delayed' : 'time';
    const timeDisplay = departure.minutesUntil <= 0 ? 'Due' : `${departure.minutesUntil} min`;
    const sourceIndicator = departure.isRealTime
        ? '<span class="source-badge realtime">Live</span>'
        : '<span class="source-badge scheduled">Scheduled</span>';

    const occupancy = departure.occupancy ? OCCUPANCY_DISPLAY[departure.occupancy] : undefined;
    const occupancyBadge = occupancy
        ? `<span class="occupancy-badge ${occupancy.className}">${occupancy.label}</span>`
        : '';

    return `
        <div class="departure-row">
            <span class="line-badge">${escapeHtml(departure.line)}</span>
            <span class="destination">${escapeHtml(departure.destination)}</span>
            <span class="time-container">
                ${occupancyBadge}
                ${sourceIndicator}
                <span class="${timeClass}">${timeDisplay}</span>
            </span>
        </div>
    `;
}

/**
 * Render a single departure board card
 */
function renderDepartureCard(board: DepartureBoard): string {
    const stopName = escapeHtml(board.stop.commonName);
    const atcoCode = escapeHtml(board.stop.atcoCode);
    const indicator = board.stop.indicator ? ` (${escapeHtml(board.stop.indicator)})` : '';
    const bearing = board.stop.bearing ? ` · ${getBearingLabel(board.stop.bearing)}` : '';

    const isFavorite = FavoritesManager.isFavorite(board.stop.atcoCode);
    const favoriteClass = isFavorite ? 'favorite-btn active' : 'favorite-btn';
    const favoriteText = isFavorite ? FAVORITED_ICON : FAVORITE_ICON;
    const favoriteAriaPressed = isFavorite ? 'true' : 'false';
    const favoriteAriaLabel = isFavorite
        ? `Remove ${stopName} from favorites`
        : `Add ${stopName} to favorites`;

    const departuresHtml =
        board.departures.length > 0
            ? board.departures.map(renderDeparture).join('')
            : '<p class="no-departures">No buses expected soon</p>';

    const directionsUrl = getDirectionsUrl(board.stop.coordinates);

    return `
        <div class="card" data-atco-code="${atcoCode}">
            <div class="stop-header">
                <h2>${stopName}${indicator}</h2>
            </div>
            <div class="card-meta">
                <span class="distance">${formatDistance(board.stop.distanceMeters)}${bearing}</span>
                <a href="${directionsUrl}" class="directions-link" target="_blank" rel="noopener" aria-label="Get walking directions to this stop">Directions</a>
                <button class="${favoriteClass}" data-atco-code="${atcoCode}" aria-pressed="${favoriteAriaPressed}" aria-label="${favoriteAriaLabel}" title="${favoriteAriaLabel}">${favoriteText}</button>
            </div>
            <div class="departures-list">${departuresHtml}</div>
        </div>
    `;
}

/**
 * Render a single train departure row
 */
function renderTrainDeparture(departure: TrainDeparture): string {
    const timeDisplay = departure.minutesUntil <= 0 ? 'Due' : `${departure.minutesUntil} min`;
    const sourceIndicator = departure.isRealTime
        ? '<span class="source-badge realtime">Live</span>'
        : '<span class="source-badge scheduled">Scheduled</span>';

    let timeClass = 'time';
    let statusBadge = '';

    if (departure.status === 'cancelled') {
        timeClass = 'time cancelled';
        statusBadge = '<span class="status-badge cancelled">Cancelled</span>';
    } else if (departure.status === 'delayed') {
        timeClass = 'time delayed';
    }

    const platformBadge = departure.platform
        ? `<span class="platform-badge">Plat ${escapeHtml(departure.platform)}</span>`
        : '';

    return `
        <div class="departure-row train-departure-row">
            ${platformBadge}
            <span class="destination">${escapeHtml(departure.destination)}</span>
            <span class="time-container">
                ${statusBadge}
                ${sourceIndicator}
                <span class="${timeClass}">${timeDisplay}</span>
            </span>
        </div>
    `;
}

/**
 * Render a single train station card with departures
 */
function renderTrainStationCard(board: TrainDepartureBoard, errorMessage?: string): string {
    const { station, departures } = board;

    let departuresHtml: string;
    if (errorMessage) {
        departuresHtml = `<p class="no-departures error-message">${escapeHtml(errorMessage)}</p>`;
    } else if (departures.length > 0) {
        departuresHtml = departures.map(renderTrainDeparture).join('');
    } else {
        departuresHtml = '<p class="no-departures">No trains expected soon</p>';
    }

    const directionsUrl = getDirectionsUrl(station.coordinates);

    const isFavorite = FavoritesManager.isStationFavorite(station.crsCode);
    const favoriteClass = isFavorite ? 'favorite-btn active' : 'favorite-btn';
    const favoriteText = isFavorite ? FAVORITED_ICON : FAVORITE_ICON;
    const favoriteAriaPressed = isFavorite ? 'true' : 'false';
    const favoriteAriaLabel = isFavorite
        ? `Remove ${station.name} from favorites`
        : `Add ${station.name} to favorites`;

    return `
        <div class="card train-station-card" data-crs-code="${station.crsCode}">
            <svg class="national-rail-logo" viewBox="23.62 263.53 179.5 108.7" aria-label="National Rail" role="img">
                <path d="m203.1 289.5v16.54h-48.42l-47.24 23.62h95.67v16.54h-95.67l62.6 25.98h-42.52l-55.51-25.98h-48.42v-16.54h48.42l47.24-23.62h-95.67v-16.54h95.67l-62.6-25.98h42.52l55.51 25.98z" fill="#e21836"/>
            </svg>
            <div class="stop-header">
                <h2>${station.name}</h2>
                <span class="station-badge">${station.crsCode}</span>
            </div>
            <div class="card-meta">
                <span class="distance">${formatDistance(station.distanceMeters)}</span>
                <a href="${directionsUrl}" class="directions-link" target="_blank" rel="noopener" aria-label="Get walking directions to this station">Directions</a>
                <button class="${favoriteClass}" data-crs-code="${station.crsCode}" aria-pressed="${favoriteAriaPressed}" aria-label="${favoriteAriaLabel}" title="${favoriteAriaLabel}">${favoriteText}</button>
            </div>
            <div class="departures-list">${departuresHtml}</div>
        </div>
    `;
}

/**
 * Render a display item (bus or train)
 */
function renderDisplayItem(item: DisplayItem): string {
    if (item.type === 'train') {
        return renderTrainStationCard(item.data, item.errorMessage);
    }
    return renderDepartureCard(item.data);
}

/**
 * Get distance from a display item
 */
function getItemDistance(item: DisplayItem): number {
    if (item.type === 'train') {
        return item.data.station.distanceMeters;
    }
    return item.data.stop.distanceMeters;
}

/**
 * Render the cards and empty state
 */
function renderItems(items: DisplayItem[], emptyMessage = 'No departures available'): string {
    if (items.length === 0) {
        return `<div class="card"><p class="no-departures">${emptyMessage}</p></div>`;
    }

    return items.map(renderDisplayItem).join('');
}

/**
 * Filter display items down to departures matching the selected bus routes.
 * An empty selection means no filter is active, so everything is shown.
 * Train items are never filtered - route selection only applies to buses.
 */
function applyRouteFilter(items: DisplayItem[]): DisplayItem[] {
    const selectedRoutes = getSelectedRoutes();
    if (selectedRoutes.size === 0) return items;

    return items.reduce<DisplayItem[]>((filtered, item) => {
        if (item.type === 'train') {
            filtered.push(item);
            return filtered;
        }

        const departures = item.data.departures.filter(d => selectedRoutes.has(d.line));
        if (departures.length > 0) {
            filtered.push({ type: 'bus', data: { ...item.data, departures } });
        }
        return filtered;
    }, []);
}

/**
 * Render a bar saying which routes the list is filtered to, with a way to clear it
 */
function renderFilterStatus(): string {
    const selectedRoutes = [...getSelectedRoutes()].sort((a, b) =>
        a.localeCompare(b, undefined, { numeric: true })
    );
    if (selectedRoutes.length === 0) return '';

    return `
        <div class="filter-status">
            <span>Showing routes ${selectedRoutes.map(escapeHtml).join(', ')}</span>
            <button id="filter-status-clear" class="filter-status-clear" type="button">Show all</button>
        </div>
    `;
}

/**
 * Display all items (bus departures and train stations) sorted by distance
 * Favorites are pinned to the top
 * @param items - The display items to render
 * @param hasMoreStops - Whether there are potentially more stops to load
 * @param onSetupHandlers - Callback to set up event handlers after rendering
 */
export function displayItems(
    items: DisplayItem[],
    hasMoreStops: boolean,
    onSetupHandlers: () => void
): void {
    const container = document.getElementById('departures-container');
    const errorCard = document.getElementById('error-card');
    const refreshContainer = document.getElementById('refresh-container');

    if (!container || !errorCard || !refreshContainer) {
        return;
    }

    // Hide error, show departures
    errorCard.style.display = 'none';
    refreshContainer.style.display = 'block';

    // Get favorites and config for sorting
    const favoriteAtcoCodes = FavoritesManager.getAtcoCodes();
    const favoriteCrsCodes = FavoritesManager.getCrsCodes();
    const config = getConfig();
    const nearbyThreshold = config.busStops.nearbyPriorityRadius;

    const isFav = (item: DisplayItem): boolean =>
        (item.type === 'bus' && favoriteAtcoCodes.has(item.data.stop.atcoCode)) ||
        (item.type === 'train' && favoriteCrsCodes.has(item.data.station.crsCode));

    // Sort: nearby stops first, then favorites, then by distance
    // A nearby non-favorite beats a distant favorite
    const sorted = [...items].sort((a, b) => {
        const aIsFav = isFav(a);
        const bIsFav = isFav(b);

        const aDistance = getItemDistance(a);
        const bDistance = getItemDistance(b);
        const aIsNearby = aDistance <= nearbyThreshold;
        const bIsNearby = bDistance <= nearbyThreshold;

        // Both same favorite status: sort by distance
        if (aIsFav === bIsFav) {
            return aDistance - bDistance;
        }

        // One is favorite, one is not
        // Nearby non-favorite beats distant favorite
        if (!aIsFav && aIsNearby && !bIsNearby) return -1;
        if (!bIsFav && bIsNearby && !aIsNearby) return 1;

        // Otherwise favorites first
        if (aIsFav) return -1;
        return 1;
    });

    // Apply route filter (empty selection shows everything)
    const selectedRoutesCount = getSelectedRoutes().size;
    const filtered = applyRouteFilter(sorted);
    const emptyMessage =
        selectedRoutesCount > 0 ? 'No buses match your selected routes' : 'No departures available';
    let html = renderFilterStatus() + renderItems(filtered, emptyMessage);

    // Add "Show more stops" button if applicable, or a closing note once the
    // search has been expanded as far as it will go
    const reachedMax = hasReachedMaxRadius();
    if (hasMoreStops && !reachedMax) {
        const currentRadius = getCurrentSearchRadius();

        html += `
            <div id="show-more-container" class="show-more-container">
                <button id="show-more-btn" class="show-more-btn" aria-label="${SHOW_MORE_LABEL}">${SHOW_MORE_LABEL}</button>
                <p class="show-more-hint">Showing stops within ${formatRadius(currentRadius)}</p>
            </div>
        `;
    } else if (reachedMax) {
        html += `
            <div id="show-more-container" class="show-more-container">
                <p class="show-more-hint">That's all stops within ${formatRadius(config.busStops.maxExpandedRadius)}</p>
            </div>
        `;
    }

    container.innerHTML = html;

    // Track displayed ATCOcodes for "show more" feature
    const atcoCodes = items
        .filter((item): item is DisplayItem & { type: 'bus' } => item.type === 'bus')
        .map(item => item.data.stop.atcoCode);
    setDisplayedAtcoCodes(atcoCodes);

    // Store items for re-rendering after favorite toggle or filter change
    setAllDisplayItems(items);

    // Set up event handlers
    onSetupHandlers();
}

/**
 * Show loading state in departures container
 */
export function showLoadingDepartures(): void {
    const container = document.getElementById('departures-container');
    if (container) {
        container.innerHTML =
            '<div class="card"><p><span class="spinner" aria-hidden="true"></span>Loading departure times...</p></div>';
    }
}

/**
 * Display error message
 */
export function displayError(message: string): void {
    const container = document.getElementById('departures-container');
    const errorCard = document.getElementById('error-card');
    const errorMessage = document.getElementById('error-message');
    const refreshContainer = document.getElementById('refresh-container');

    if (!container || !errorCard || !errorMessage || !refreshContainer) {
        return;
    }

    container.innerHTML = '';
    refreshContainer.style.display = 'none';
    errorCard.style.display = 'block';
    errorMessage.textContent = message;
}

/**
 * Show manual postcode entry form with a message
 */
export function showPostcodeEntryForm(
    message: string,
    defaultPostcode?: string,
    shouldFocus = true
): void {
    const postcodeDisplay = document.getElementById('postcode-display');
    const postcodeForm = document.getElementById('postcode-form');
    const postcodeInput = document.getElementById('postcode-input') as HTMLInputElement | null;

    if (postcodeDisplay) {
        postcodeDisplay.textContent = message;
    }
    if (postcodeForm) {
        postcodeForm.style.display = 'block';
    }
    if (postcodeInput && defaultPostcode) {
        postcodeInput.value = defaultPostcode;
    }
    if (shouldFocus) {
        postcodeInput?.focus();
    }
}

/**
 * Update postcode display
 */
export function updatePostcodeDisplay(text: string, isHtml = false): void {
    const postcodeDisplay = document.getElementById('postcode-display');
    if (postcodeDisplay) {
        if (isHtml) {
            postcodeDisplay.innerHTML = text;
        } else {
            postcodeDisplay.textContent = text;
        }
    }
}

/**
 * Hide postcode form
 */
export function hidePostcodeForm(): void {
    const postcodeForm = document.getElementById('postcode-form');
    if (postcodeForm) {
        postcodeForm.style.display = 'none';
    }
}

/**
 * Show postcode error message
 */
export function showPostcodeError(message: string): void {
    const errorElement = document.getElementById('postcode-error');
    const input = document.getElementById('postcode-input');

    if (errorElement) {
        errorElement.textContent = message;
        errorElement.style.display = 'block';
    }
    if (input) {
        input.setAttribute('aria-invalid', 'true');
    }
}

/**
 * Clear postcode error message
 */
export function clearPostcodeError(): void {
    const errorElement = document.getElementById('postcode-error');
    const input = document.getElementById('postcode-input');

    if (errorElement) {
        errorElement.textContent = '';
        errorElement.style.display = 'none';
    }
    if (input) {
        input.setAttribute('aria-invalid', 'false');
    }
}

/**
 * Set postcode form busy state
 */
export function setPostcodeFormBusy(busy: boolean): void {
    const form = document.getElementById('postcode-form');
    if (form) {
        form.setAttribute('aria-busy', String(busy));
    }
}

/**
 * Show refresh container
 */
export function showRefreshContainer(): void {
    const refreshContainer = document.getElementById('refresh-container');
    if (refreshContainer) {
        refreshContainer.style.display = 'block';
    }
}
